import { useApiPost } from './useApiPost'
import { useApiPut } from './useApiPut'
import { useApiPatch } from './useApiPatch'
import { useApiDelete } from './useApiDelete'
import { useApiCall } from './useApi'
import { useToast } from '~/components/ui/toast/use-toast'

export type RateAlertOperation = 'buy' | 'sell'
export type RateType = 'regular' | 'cashless'
export type RateAlertCondition = 'lte' | 'gte'

export interface IRateAlertCreate {
  currency_code: string
  type: RateAlertOperation
  value: number
  condition: RateAlertCondition
  bank_id?: number
  expired_at?: string | null
  rate_type?: RateType
  is_active?: '0' | '1'
}

export interface IRateAlert extends Omit<IRateAlertCreate, 'is_active'> {
  id: number
  created_at: string
  bank_name: string
  is_active: boolean
  rate_type?: RateType
}

export interface IRateAlertHistory {
  id: number
  user_id: number
  currency: string
  value: string
  subscription_id: number
  created_at: string
}

export interface IRateAlertUpdate extends Partial<Omit<IRateAlertCreate, 'currency_code'>> {
  is_active?: boolean
  rate_type?: RateType
  condition?: RateAlertCondition
}

export function useRateAlerts() {
  const { t } = useI18n()
  const { toast } = useToast()

  const createRateAlert = async (data: IRateAlertCreate) => {
    const { data: response, error } = await useApiPost<{
      message: string
      data: { id: number }
    }>('/my-alerts/rates', data)

    if (error) {
      const errorMessage =
        (error as { _data?: { message?: string } })._data?.message || t('rate_alert.create_error')

      toast({
        title: t('rate_alert.create_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      throw new Error(errorMessage)
    }

    toast({
      title: t('rate_alert.create_success_title'),
      description: t('rate_alert.create_success_description'),
      variant: 'success'
    })

    return response?.data
  }

  const validateRateTarget = (currentRate: number, targetRate: number): boolean => {
    const minValidRate = currentRate * 0.5
    const maxValidRate = currentRate * 1.5

    return targetRate >= minValidRate && targetRate <= maxValidRate
  }

  const updateRateAlert = async (id: number, data: IRateAlertUpdate) => {
    const { data: response, error } = await useApiPut<{
      message: string
      data: { id: number }
    }>(`/my-alerts/rates/${id}`, data)

    if (error) {
      const errorMessage =
        (error as { _data?: { message?: string } })._data?.message || t('rate_alert.update_error')

      toast({
        title: t('rate_alert.update_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      throw new Error(errorMessage)
    }

    toast({
      title: t('rate_alert.update_success_title'),
      description: t('rate_alert.update_success_description'),
      variant: 'success'
    })

    return response?.data
  }

  const toggleRateAlert = async (id: number, is_active: boolean) => {
    const { data: response, error } = await useApiPatch<{
      message: string
      data: { id: number }
    }>(`/my-alerts/rates/${id}/toggle-active`, { is_active })

    if (error) {
      const errorMessage =
        (error as { _data?: { message?: string } })._data?.message || t('rate_alert.update_error')

      toast({
        title: t('rate_alert.update_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      throw new Error(errorMessage)
    }

    return response?.data
  }

  const deleteRateAlert = async (id: number) => {
    const { data: response, error } = await useApiDelete<{
      message: string
    }>(`/my-alerts/rates/${id}`)

    if (error) {
      const errorMessage =
        (error as { _data?: { message?: string } })._data?.message || t('rate_alert.delete_error')

      toast({
        title: t('rate_alert.delete_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      throw new Error(errorMessage)
    }

    toast({
      title: t('rate_alert.delete_success_title'),
      description: t('rate_alert.delete_success_description'),
      variant: 'success'
    })

    return response?.data
  }

  const fetchRateAlerts = async (): Promise<{
    data: IRateAlert[] | null
    error: unknown | null
  }> => {
    try {
      const response = await useApiCall<IRateAlert[]>('/my-alerts/rates')

      return { data: response, error: null }
    } catch (err: unknown) {
      const errorMessage =
        (err as { _data?: { message?: string } })._data?.message || t('rate_alert.fetch_error')

      toast({
        title: t('rate_alert.fetch_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      return { data: null, error: err }
    }
  }

  const fetchRateAlertsHistory = async (): Promise<{
    data: IRateAlertHistory[] | null
    error: unknown | null
  }> => {
    try {
      const response = await useApiCall<IRateAlertHistory[]>('/my-alerts/rates/history')

      return { data: response, error: null }
    } catch (err: unknown) {
      const errorMessage =
        (err as { _data?: { message?: string } })._data?.message ||
        t('rate_alert.fetch_history_error')

      toast({
        title: t('rate_alert.fetch_history_error_title'),
        description: errorMessage,
        variant: 'destructive'
      })

      return { data: null, error: err }
    }
  }

  return {
    createRateAlert,
    updateRateAlert,
    toggleRateAlert,
    deleteRateAlert,
    validateRateTarget,
    fetchRateAlerts,
    fetchRateAlertsHistory
  }
}
